/**
 * MigrationHistoryPage
 * 서버 이전 이력 타임라인 페이지
 */

import React, { useState, useEffect } from 'react';

interface MigrationHistory {
  id: string;
  service_name: string;
  from_server?: string;
  to_server: string;
  from_spec?: string;
  to_spec?: string;
  reason?: string;
  migrated_at: string;
  notes?: string;
  created_at?: string;
}

interface MigrationFormData {
  service_name: string;
  from_server: string;
  to_server: string;
  from_spec: string;
  to_spec: string;
  reason: string;
  migrated_at: string;
  notes: string;
}

export const MigrationHistoryPage: React.FC = () => {
  const [migrations, setMigrations] = useState<MigrationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<MigrationFormData>({
    service_name: '',
    from_server: '',
    to_server: '',
    from_spec: '',
    to_spec: '',
    reason: '',
    migrated_at: new Date().toISOString().slice(0, 16),
    notes: '',
  });

  const fetchMigrations = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/migrations', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('이전 이력 조회 실패');

      const data = await response.json();
      setMigrations(data.migrations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMigrations();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.service_name.trim() || !form.to_server.trim()) {
      setError('서비스명과 이전 후 서버는 필수입니다');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const body: Record<string, any> = { ...form };
      Object.keys(body).forEach(key => {
        if (body[key] === '') body[key] = null;
      });
      body.service_name = form.service_name.trim();
      body.to_server = form.to_server.trim();
      body.migrated_at = form.migrated_at;

      const response = await fetch('/api/admin/migrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('저장 실패');

      setShowForm(false);
      setForm({
        service_name: '',
        from_server: '',
        to_server: '',
        from_spec: '',
        to_spec: '',
        reason: '',
        migrated_at: new Date().toISOString().slice(0, 16),
        notes: '',
      });
      fetchMigrations();
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 서비스별 색상
  const getServiceColor = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes('newattend')) return 'bg-blue-500';
    if (lower.includes('ntd')) return 'bg-purple-500';
    if (lower.includes('taskbuddy')) return 'bg-green-500';
    if (lower.includes('smartbid')) return 'bg-orange-500';
    return 'bg-gray-500';
  };

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">서버 이전 이력</h1>
          <p className="text-sm text-gray-600 mt-1">NTD 서비스 서버 이전/마이그레이션 기록</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <span>+</span>
          <span>이전 이력 추가</span>
        </button>
      </div>

      {/* 에러 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* 추가 폼 */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">이전 이력 추가</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">서비스명 *</label>
                <input
                  type="text"
                  name="service_name"
                  value={form.service_name}
                  onChange={handleChange}
                  placeholder="예: NewAttend, NTD Manager"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이전 일시 *</label>
                <input
                  type="datetime-local"
                  name="migrated_at"
                  value={form.migrated_at}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이전 전 서버</label>
                <input
                  type="text"
                  name="from_server"
                  value={form.from_server}
                  onChange={handleChange}
                  placeholder="예: EC2 (3.39.48.110)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이전 후 서버 *</label>
                <input
                  type="text"
                  name="to_server"
                  value={form.to_server}
                  onChange={handleChange}
                  placeholder="예: NTD Server (192.168.0.38)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이전 전 스펙</label>
                <input
                  type="text"
                  name="from_spec"
                  value={form.from_spec}
                  onChange={handleChange}
                  placeholder="예: t3.micro, 2vCPU, 1GB RAM"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이전 후 스펙</label>
                <input
                  type="text"
                  name="to_spec"
                  value={form.to_spec}
                  onChange={handleChange}
                  placeholder="예: N150, 4코어, 16GB RAM, 466GB SSD"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">이전 사유</label>
              <input
                type="text"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                placeholder="예: 비용 절감, 성능 개선"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">비고</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? '저장 중...' : '추가'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 타임라인 */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">로딩 중...</div>
      ) : migrations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          서버 이전 이력이 없습니다.
        </div>
      ) : (
        <div className="relative">
          {/* 타임라인 세로선 */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

          <div className="space-y-8">
            {migrations.map((migration) => (
              <div key={migration.id} className="relative flex gap-6">
                {/* 타임라인 점 */}
                <div className={`relative z-10 w-16 h-16 flex-shrink-0 rounded-full ${getServiceColor(migration.service_name)} flex items-center justify-center`}>
                  <span className="text-white text-lg font-bold">
                    {migration.service_name.charAt(0).toUpperCase()}
                  </span>
                </div>

                {/* 카드 */}
                <div className="flex-1 bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{migration.service_name}</h3>
                      <p className="text-sm text-gray-500">{formatDate(migration.migrated_at)}</p>
                    </div>
                    {migration.reason && (
                      <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        {migration.reason}
                      </span>
                    )}
                  </div>

                  {/* 이전 경로 */}
                  <div className="flex items-center gap-4 mb-3">
                    {migration.from_server && (
                      <div className="flex-1 bg-red-50 rounded-lg p-3">
                        <div className="text-xs text-red-600 font-medium mb-1">이전 전</div>
                        <div className="text-sm font-medium text-red-900">{migration.from_server}</div>
                        {migration.from_spec && (
                          <div className="text-xs text-red-700 mt-1">{migration.from_spec}</div>
                        )}
                      </div>
                    )}
                    <div className="text-gray-400 text-2xl flex-shrink-0">→</div>
                    <div className="flex-1 bg-green-50 rounded-lg p-3">
                      <div className="text-xs text-green-600 font-medium mb-1">이전 후</div>
                      <div className="text-sm font-medium text-green-900">{migration.to_server}</div>
                      {migration.to_spec && (
                        <div className="text-xs text-green-700 mt-1">{migration.to_spec}</div>
                      )}
                    </div>
                  </div>

                  {migration.notes && (
                    <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                      {migration.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 건수 표시 */}
      {!loading && migrations.length > 0 && (
        <div className="mt-6 text-sm text-gray-500 text-center">
          총 {migrations.length}건의 이전 이력
        </div>
      )}
    </div>
  );
};
